import { DB_TYPE, EGrammarType } from '@quesmed/types';
import {
  EBatchType,
  EContentType,
  EDifficultyType,
  EEntitlementType,
  EMockTestType,
  EOsceType,
  EPaceType,
  EProductDuration,
  EProductType,
  EQuestionType,
  ETopicType,
} from '@quesmed/types/models';
import type { ISelectItemSection } from '../core/ui/Form/Select/Select';
import { getSelectItem } from '../utils/array';

export enum EProductCategory {
  ALL = 'ALL',
  UNDERGRAD = 'UNDERGRAD',
  POSTGRAD = 'POSTGRAD',
  CLINICAL = 'CLINICAL',
  STUDENT = 'STUDENT',
}

export enum EProductName {
  OSCE = 'OSCE',
  QBANK = 'QBANK',
  PRE_CLINICAL = 'PRE CLINICAL',
  ANATOMY = 'ANATOMY',
  MSRA = 'MSRA',
  MRCP = 'MRCP',
  PLAB1 = 'PLAB1',
  PLAB2 = 'PLAB2',
  MRCP_PART1 = 'MRCP PART 1',
  MRCP_PART2 = 'MRCP PART 2',
  MOCK_TEST = 'Mock Test',
  PACES = 'PACES',
  OTHER = 'OTHER PRODUCTS',
  INTERVIEW = 'INTERVIEW',
  MRCP_VIDEO = 'MRCP VIDEO',
  UCAT = 'UCAT',
  VERBAL_REASONING = 'VERBAL REASONING',
  DECISION_MAKING = 'DECISION MAKING',
  QUANTITATIVE_REASONING = 'QUANTITATIVE REASONING',
  SITUATIONAL_JUDGEMENT = 'SITUATIONAL JUDGEMENT',
}

export enum EDateFilter {
  ALL = 0,
  TODAY = 1,
  YESTERDAY = 2,
  THIS_WEEK = 3,
  LAST_WEEK = 4,
  PAST_TWO_WEEKS = 5,
  THIS_MONTH = 6,
  LAST_MONTH = 7,
  LAST_THREE_MONTHS = 8,
  LAST_SIX_MONTHS = 9,
  LAST_TWELVE_MONTHS = 10,
  THIS_YEAR = 11,
  LAST_YEAR = 12,
}

const productOptions: Partial<Record<EBatchType, ISelectItemSection[]>> = {
  [EBatchType.QUESTION]: [
    {
      label: EProductName.QBANK,
      items: getSelectItem(EEntitlementType, [
        EEntitlementType.CLINICAL,
        EEntitlementType.DATA_INTERPRETATION,
      ]),
    },
    {
      label: EProductName.PRE_CLINICAL,
      items: getSelectItem(EEntitlementType, [
        EEntitlementType.PRE_CLINICAL,
        EEntitlementType.CLINICAL_SUBSET,
      ]),
    },
    {
      label: EProductName.ANATOMY,
      items: getSelectItem(EEntitlementType, [EEntitlementType.ANATOMY]),
    },
    {
      label: EProductName.MSRA,
      items: getSelectItem(EEntitlementType, [
        EEntitlementType.PROFESSIONAL_DILEMMA,
        EEntitlementType.CLINICAL_PROBLEM_SOLVING,
        EEntitlementType.EXTENDED_MATCHING_QUESTIONS,
      ]),
    },
    {
      label: EProductName.MRCP,
      items: getSelectItem(EEntitlementType, [EEntitlementType.MRCP_PART_1, EEntitlementType.MRCP_PART_2]),
    },
    {
      label: EProductName.PLAB1,
      items: getSelectItem(EEntitlementType, [EEntitlementType.PLAB_1]),
    },
    {
      label: EProductName.UCAT,
      items: getSelectItem(EEntitlementType, [
        EEntitlementType.VERBAL_REASONING,
        EEntitlementType.DECISION_MAKING,
        EEntitlementType.QUANTITATIVE_REASONING,
        EEntitlementType.SITUATIONAL_JUDGEMENT,
      ]),
    },
  ],
  [EBatchType.CHAPTER]: [
    {
      label: EProductName.OTHER,
      items: getSelectItem(EEntitlementType, [EEntitlementType.ALL]),
    },
    {
      label: EProductName.PACES,
      items: getSelectItem(EEntitlementType, [
        EEntitlementType.ABDOMINAL,
        EEntitlementType.CARDIOVASCULAR,
        EEntitlementType.COMMUNICATION,
        EEntitlementType.CONSULTATION,
        EEntitlementType.NEUROLOGY,
        EEntitlementType.RESPIRATORY,
      ]),
    },
  ],
  [EBatchType.MOCK_TEST]: [
    {
      label: EProductName.QBANK,
      items: getSelectItem(EProductType, [EProductType.QBANK]),
    },
    {
      label: EProductName.PRE_CLINICAL,
      items: getSelectItem(EProductType, [EProductType.MEDICAL_SCIENCES]),
    },
    {
      label: EProductName.ANATOMY,
      items: getSelectItem(EProductType, [EProductType.ANATOMY]),
    },
    {
      label: EProductName.PLAB1,
      items: getSelectItem(EProductType, [EProductType.PLAB1]),
    },
    {
      label: EProductName.MRCP,
      items: getSelectItem(EProductType, [EProductType.MRCP_PART1, EProductType.MRCP_PART2]),
    },
  ],
  [EBatchType.STATION]: [
    {
      label: EProductName.OSCE,
      items: getSelectItem(EEntitlementType, [
        EEntitlementType.HISTORY_TAKING,
        EEntitlementType.COMMUNICATION_SKILLS,
        EEntitlementType.EXAMINATION,
        EEntitlementType.PROCEDURE,
        EEntitlementType.INTERPRETATION,
        EEntitlementType.ABCDE,
        EEntitlementType.PRESCRIBING,
        EEntitlementType.MIXED,
      ]),
    },
    {
      label: EProductName.PACES,
      items: getSelectItem(EEntitlementType, [
        EEntitlementType.ABDOMINAL,
        EEntitlementType.CARDIOVASCULAR,
        EEntitlementType.COMMUNICATION,
        EEntitlementType.CONSULTATION,
        EEntitlementType.NEUROLOGY,
        EEntitlementType.RESPIRATORY,
      ]),
    },
    {
      label: EProductName.PLAB2,
      items: getSelectItem(EEntitlementType, [
        EEntitlementType.INTERPRETATION,
        EEntitlementType.ABCDE,
        EEntitlementType.PRESCRIBING,
        EEntitlementType.MIXED,
      ]),
    },
  ],
};

const templates: Partial<Record<EGrammarType, Record<EContentType, string>>> = {
  [EGrammarType.CONCEPT]: {
    [EContentType.MARKDOWN]: '/templates/book/concept/concept-grammar-md',
    [EContentType.CSV]: '/templates/book/concept/concept-grammar-csv',
  },
  // [EGrammarType.BOOK]: {
  //   [EContentType.MARKDOWN]: '/templates/book/book/book-grammar-md',
  //   [EContentType.CSV]: '/templates/book/book/book-grammar-csv',
  // },
  [EGrammarType.OSCE]: {
    [EContentType.MARKDOWN]: '/templates/station/osce/osce-grammar-md',
    [EContentType.CSV]: '/templates/station/osce/osce-grammar-csv',
  },
  [EGrammarType.PACES]: {
    [EContentType.MARKDOWN]: '/templates/station/pace/pace-grammar-md',
    [EContentType.CSV]: '/templates/station/pace/pace-grammar-csv',
  },

  [EGrammarType.EMQ_QUESTION]: {
    [EContentType.MARKDOWN]: '/templates/question/emq/emq-grammar-md',
    [EContentType.CSV]: '/templates/question/emq/emq-grammar-csv',
  },
  [EGrammarType.RA_QUESTION]: {
    [EContentType.MARKDOWN]: '/templates/question/ra/ra-grammar-md',
    [EContentType.CSV]: '/templates/question/ra/ra-grammar-csv',
  },
  [EGrammarType.SJT_QUESTION]: {
    [EContentType.MARKDOWN]: '/templates/question/sjt/sjt-grammar-md',
    [EContentType.CSV]: '/templates/question/sjt/sjt-grammar-csv',
  },
  [EGrammarType.QUESTION]: {
    [EContentType.MARKDOWN]: '/templates/question/sba/sba-grammar-md',
    [EContentType.CSV]: '/templates/question/sba/sba-grammar-csv',
  },
  [EGrammarType.VERBAL_VERACITY]: {
    [EContentType.MARKDOWN]: '/templates/question/ucat/verbal/veracity-grammar-md',
    [EContentType.CSV]: '',
  },
  [EGrammarType.VERBAL_READING_COMPREHENSION]: {
    [EContentType.MARKDOWN]: '/templates/question/ucat/verbal/reading-grammar-md',
    [EContentType.CSV]: '',
  },
  [EGrammarType.QUANTITATIVE_REASONING_SBA]: {
    [EContentType.MARKDOWN]: '/templates/question/ucat/quantitative/sba-grammar-md',
    [EContentType.CSV]: '',
  },
  [EGrammarType.DECISION_MAKING_SBA]: {
    [EContentType.MARKDOWN]: '/templates/question/ucat/decision/sba-grammar-md',
    [EContentType.CSV]: '',
  },
  [EGrammarType.DECISION_MAKING_YES_NO]: {
    [EContentType.MARKDOWN]: '/templates/question/ucat/decision/yes-no-grammar-md',
    [EContentType.CSV]: '',
  },
  [EGrammarType.SJT_SINGLE_CHOICE]: {
    [EContentType.MARKDOWN]: '/templates/question/ucat/sjt/single-choice-grammar-md',
    [EContentType.CSV]: '',
  },
  [EGrammarType.SJT_TWO_ANSWERS]: {
    [EContentType.MARKDOWN]: '/templates/question/ucat/sjt/two-answer-grammar-md',
    [EContentType.CSV]: '',
  },
};

const templateGrammars = {
  chapterGrammar: {
    label: EBatchType[EBatchType.CHAPTER],
    items: getSelectItem(EGrammarType, [EGrammarType.CONCEPT]),
  },
  paceGrammar: {
    label: EBatchType[EBatchType.STATION],
    items: getSelectItem(EGrammarType, [EGrammarType.PACES]),
  },
  osceGrammar: {
    label: EBatchType[EBatchType.STATION],
    items: getSelectItem(EGrammarType, [EGrammarType.OSCE]),
  },
  msraGrammar: {
    label: EBatchType[EBatchType.QUESTION],
    items: getSelectItem(EGrammarType, [
      EGrammarType.QUESTION,
      EGrammarType.RA_QUESTION,
      EGrammarType.SJT_QUESTION,
      EGrammarType.EMQ_QUESTION,
    ]),
  },
  questionGrammar: {
    label: EBatchType[EBatchType.QUESTION],
    items: getSelectItem(EGrammarType, [EGrammarType.QUESTION]),
  },
  verbalGrammar: {
    label: EBatchType[EBatchType.QUESTION],
    items: getSelectItem(EGrammarType, [
      EGrammarType.VERBAL_VERACITY,
      EGrammarType.VERBAL_READING_COMPREHENSION,
    ]),
  },
  quantitativeGrammar: {
    label: EBatchType[EBatchType.QUESTION],
    items: getSelectItem(EGrammarType, [EGrammarType.QUANTITATIVE_REASONING_SBA]),
  },
  decisionGrammar: {
    label: EBatchType[EBatchType.QUESTION],
    items: getSelectItem(EGrammarType, [
      EGrammarType.DECISION_MAKING_SBA,
      EGrammarType.DECISION_MAKING_YES_NO,
    ]),
  },
  sjtGrammar: {
    label: EBatchType[EBatchType.QUESTION],
    items: getSelectItem(EGrammarType, [EGrammarType.SJT_SINGLE_CHOICE, EGrammarType.SJT_TWO_ANSWERS]),
  },
};

const grammarOptions: Partial<Record<EBatchType, ISelectItemSection[]>> = {
  [EBatchType.QUESTION]: [
    {
      label: EProductName.OTHER,
      items: getSelectItem(EGrammarType, [EGrammarType.QUESTION]),
    },
    {
      label: EProductName.MSRA,
      items: getSelectItem(EGrammarType, [
        EGrammarType.RA_QUESTION,
        EGrammarType.EMQ_QUESTION,
        EGrammarType.SJT_QUESTION,
      ]),
    },
    {
      label: EProductName.VERBAL_REASONING,
      items: getSelectItem(EGrammarType, [
        EGrammarType.VERBAL_VERACITY,
        EGrammarType.VERBAL_READING_COMPREHENSION,
      ]),
    },
    {
      label: EProductName.DECISION_MAKING,
      items: getSelectItem(EGrammarType, [
        EGrammarType.DECISION_MAKING_SBA,
        EGrammarType.DECISION_MAKING_YES_NO,
      ]),
    },
    {
      label: EProductName.QUANTITATIVE_REASONING,
      items: getSelectItem(EGrammarType, [EGrammarType.QUANTITATIVE_REASONING_SBA]),
    },
    {
      label: EProductName.SITUATIONAL_JUDGEMENT,
      items: getSelectItem(EGrammarType, [EGrammarType.SJT_SINGLE_CHOICE, EGrammarType.SJT_TWO_ANSWERS]),
    },
  ],
  [EBatchType.MOCK_TEST]: [
    {
      label: EProductName.QBANK,
      items: getSelectItem(EGrammarType, [
        EGrammarType.QUESTION,
        EGrammarType.PSA_MULTI_QA,
        EGrammarType.PSA_PRESCRIBE,
        EGrammarType.PSA_QA,
        EGrammarType.PSA_SBA,
      ]),
    },
    {
      label: EProductName.OTHER,
      items: getSelectItem(EGrammarType, [EGrammarType.QUESTION]),
    },
  ],
  [EBatchType.STATION]: [
    {
      label: EProductName.OSCE,
      items: getSelectItem(EGrammarType, [EGrammarType.OSCE]),
    },
    {
      label: EProductName.PLAB2,
      items: getSelectItem(EGrammarType, [EGrammarType.OSCE]),
    },
    {
      label: EProductName.PACES,
      items: getSelectItem(EGrammarType, [EGrammarType.PACES, EGrammarType.PACE_TEMPLATE]),
    },
  ],
  [EBatchType.CHAPTER]: [
    {
      label: EProductName.OTHER,
      items: getSelectItem(EGrammarType, [EGrammarType.CONCEPT]),
    },
  ],
};

const topicOptions: Partial<Record<EBatchType, (ISelectItemSection & { productId?: EProductType[] })[]>> = {
  [EBatchType.QUESTION]: [
    {
      label: EProductName.QBANK,
      items: getSelectItem(ETopicType, [ETopicType.CLINICAL, ETopicType.DATA_INTERPRETATION]),
    },
    {
      label: EProductName.ANATOMY,
      items: getSelectItem(ETopicType, [ETopicType.ANATOMY]),
    },
    {
      label: EProductName.PRE_CLINICAL,
      items: getSelectItem(ETopicType, [ETopicType.PRE_CLINICAL, ETopicType.CLINICAL]),
    },
    {
      label: EProductName.MSRA,
      items: getSelectItem(ETopicType, [ETopicType.CLINICAL, ETopicType.SJT]),
    },
    {
      label: EProductName.MRCP,
      items: getSelectItem(ETopicType, [ETopicType.CLINICAL]),
    },
    {
      label: EProductName.PLAB1,
      items: getSelectItem(ETopicType, [ETopicType.CLINICAL]),
    },
  ],
  [EBatchType.MOCK_TEST]: [
    {
      label: EProductName.QBANK,
      items: getSelectItem(ETopicType, [
        ETopicType.PSA,
        ETopicType.MOCK_TEST,
        // ETopicType.DATA_INTERPRETATION,
      ]),
    },
    {
      label: EProductName.ANATOMY,
      items: getSelectItem(ETopicType, [
        // ETopicType.ANATOMY,
        ETopicType.MOCK_TEST,
      ]),
    },
    {
      label: EProductName.MRCP,
      items: getSelectItem(ETopicType, [ETopicType.CLINICAL, ETopicType.MOCK_TEST]),
    },
    {
      label: EProductName.PRE_CLINICAL,
      items: getSelectItem(ETopicType, [
        // ETopicType.PRE_CLINICAL, ETopicType.CLINICAL,
        ETopicType.MOCK_TEST,
      ]),
    },
    {
      label: EProductName.PLAB1,
      items: getSelectItem(ETopicType, [
        // ETopicType.CLINICAL,
        ETopicType.MOCK_TEST,
      ]),
    },
  ],
  [EBatchType.STATION]: [
    {
      label: EProductName.OSCE,
      items: getSelectItem(EOsceType, [
        EOsceType.HISTORY_TAKING,
        EOsceType.COMMUNICATION_SKILLS,
        EOsceType.EXAMINATION,
        EOsceType.PROCEDURE,
        EOsceType.INTERPRETATION,
        EOsceType.ABCDE,
        EOsceType.PRESCRIBING,
        EOsceType.MIXED,
      ]),
    },
    {
      label: EProductName.PLAB2,
      items: getSelectItem(EOsceType, [
        EOsceType.INTERPRETATION,
        EOsceType.ABCDE,
        EOsceType.PRESCRIBING,
        EOsceType.MIXED,
      ]),
    },
    {
      label: EProductName.PACES,
      items: getSelectItem(EPaceType, [
        EPaceType.ABDOMINAL,
        EPaceType.CARDIOVASCULAR,
        EPaceType.COMMUNICATION,
        EPaceType.CONSULTATION,
        EPaceType.NEUROLOGY,
        EPaceType.RESPIRATORY,
      ]),
    },
  ],
  [EBatchType.CHAPTER]: [
    {
      label: EProductName.QBANK,
      items: getSelectItem(ETopicType, [
        ETopicType.CLINICAL,
        ETopicType.PRE_CLINICAL,
        ETopicType.DATA_INTERPRETATION,
      ]),
      productId: [EProductType.QBANK],
    },
    {
      label: EProductName.ANATOMY,
      items: getSelectItem(ETopicType, [ETopicType.ANATOMY]),
      productId: [EProductType.ANATOMY],
    },
    {
      label: EProductName.PRE_CLINICAL,
      items: getSelectItem(ETopicType, [ETopicType.PRE_CLINICAL, ETopicType.CLINICAL]),
      productId: [EProductType.MEDICAL_SCIENCES],
    },
    {
      label: EProductName.MSRA,
      items: getSelectItem(ETopicType, [ETopicType.SJT, ETopicType.CLINICAL]),
      productId: [EProductType.MSRA],
    },
    {
      label: EProductName.MRCP,
      items: getSelectItem(ETopicType, [ETopicType.CLINICAL]),
      productId: [EProductType.MRCP_PART1, EProductType.MRCP_PART2],
    },
    {
      label: EProductName.UCAT,
      items: getSelectItem(ETopicType, [ETopicType.UCAT]),
      productId: [EProductType.UCAT],
    },
    {
      label: EProductName.PLAB1,
      items: getSelectItem(ETopicType, [ETopicType.CLINICAL]),
      productId: [EProductType.PLAB1],
    },
    // {
    //   label: EProductName.OSCE,
    //   items: getSelectItem(ETopicType, [ETopicType.OSCE]),
    // },
    {
      label: EProductName.PACES,
      items: getSelectItem(ETopicType, [ETopicType.OSCE]),
      productId: [EProductType.PACES],
    },
    {
      label: EProductName.INTERVIEW,
      items: getSelectItem(ETopicType, [
        ETopicType.INTERVIEW_ANAESTHETICS,
        ETopicType.INTERVIEW_IMT,
        ETopicType.INTERVIEW_RADIOLOGY,
        ETopicType.INTERVIEW_PAEDIATRICS,
        ETopicType.INTERVIEW_ACCS,
      ]),
      productId: [
        EProductType.INTERVIEW_ANAESTHETICS,
        EProductType.INTERVIEW_IMT,
        EProductType.INTERVIEW_RADIOLOGY,
        EProductType.INTERVIEW_PAEDIATRICS,
        EProductType.INTERVIEW_ACCS,
      ],
    },
  ],
};

const difficulty = getSelectItem(EDifficultyType, [
  EDifficultyType.QUICK_FIRE,
  EDifficultyType.CORE,
  EDifficultyType.CHALLENGING,
  EDifficultyType.DIFFICULT,
]);

const durations = getSelectItem(EProductDuration, [
  EProductDuration.ALL,
  EProductDuration.ONE,
  EProductDuration.TWO,
  EProductDuration.THREE,
  EProductDuration.SIX,
  EProductDuration.TWELVE,
]);

const mockTestTypes = [
  {
    label: EProductName.QBANK,
    items: getSelectItem(EMockTestType, [
      EMockTestType.FINALS,
      EMockTestType.UKMLA_FULL_MOCK,
      EMockTestType.MISCELLANEOUS,
      EMockTestType.UNIVERSITY_SPECIFIC,
      EMockTestType.PSA,
      EMockTestType.UKMLA_MINI_MOCK,
    ]),
  },
  {
    label: EProductName.PRE_CLINICAL,
    items: getSelectItem(EMockTestType, [EMockTestType.UNIVERSITY_SPECIFIC]),
  },
  {
    label: EProductName.ANATOMY,
    items: getSelectItem(EMockTestType, [EMockTestType.ANATOMY_SPOTTER]),
  },
  {
    label: EProductName.PLAB1,
    items: getSelectItem(EMockTestType, [EMockTestType.UKMLA_FULL_MOCK, EMockTestType.UKMLA_MINI_MOCK]),
  },
  {
    label: EProductName.MRCP,
    items: getSelectItem(EMockTestType, [EMockTestType.MRCP]),
  },
  {
    label: EProductName.UCAT,
    items: getSelectItem(EMockTestType, [
      EMockTestType.UCAT_FULL_MOCK,
      EMockTestType.QUANTITATIVE_REASONING,
      EMockTestType.VERBAL_REASONING,
      EMockTestType.DECISION_MAKING,
      EMockTestType.SITUATIONAL_JUDGEMENT,
    ]),
  },
  {
    label: EProductName.MSRA,
    items: getSelectItem(EMockTestType, [EMockTestType.MSRA_MOCK]),
  },
];

const questionTypes = [
  {
    label: EProductName.OTHER,
    items: getSelectItem(EQuestionType, [EQuestionType.SINGLE_BEST_ANSWER]),
  },
  {
    label: EProductName.MOCK_TEST,
    items: getSelectItem(EQuestionType, [
      EQuestionType.SINGLE_BEST_ANSWER,
      EQuestionType.QUESTION_ANSWER,
      EQuestionType.MULTIPLE_ANSWERS,
      EQuestionType.PRESCRIPTION_ANSWER,
    ]),
  },
  {
    label: EProductName.MSRA,
    items: getSelectItem(EQuestionType, [
      EQuestionType.EXTENDED_MATCHING_ANSWER,
      EQuestionType.SELECT_THREE_ANSWER,
      EQuestionType.RANKING_ANSWER,
    ]),
  },
  {
    label: EProductName.UCAT,
    items: getSelectItem(EQuestionType, [
      EQuestionType.VERBAL_VERACITY,
      EQuestionType.VERBAL_READING_COMPREHENSION,
      EQuestionType.QUANTITATIVE_REASONING_SBA,
      EQuestionType.DECISION_MAKING_SBA,
    ]),
  },
];

const productEntitlements = [
  {
    label: EProductName.QBANK,
    items: getSelectItem(EEntitlementType, [
      EEntitlementType.CLINICAL,
      EEntitlementType.DATA_INTERPRETATION,
      EEntitlementType.PRE_CLINICAL,
    ]),
    dbType: DB_TYPE.FINALS,
  },
  {
    label: EProductName.PRE_CLINICAL,
    items: getSelectItem(EEntitlementType, [EEntitlementType.PRE_CLINICAL, EEntitlementType.CLINICAL_SUBSET]),
    dbType: DB_TYPE.FINALS,
  },
  {
    label: EProductName.ANATOMY,
    items: getSelectItem(EEntitlementType, [EEntitlementType.ANATOMY]),
    dbType: DB_TYPE.FINALS,
  },
  {
    label: EProductName.PLAB1,
    items: getSelectItem(EEntitlementType, [EEntitlementType.PLAB_1]),
    dbType: DB_TYPE.FINALS,
  },
  {
    label: EProductName.MRCP,
    items: getSelectItem(EEntitlementType, [EEntitlementType.MRCP_PART_1, EEntitlementType.MRCP_PART_2]),
    dbType: DB_TYPE.MRCP,
  },
  {
    label: EProductName.MSRA,
    items: getSelectItem(EEntitlementType, [
      EEntitlementType.CLINICAL_PROBLEM_SOLVING,
      EEntitlementType.PROFESSIONAL_DILEMMA,
      EEntitlementType.EXTENDED_MATCHING_QUESTIONS,
    ]),
    dbType: DB_TYPE.FINALS,
  },
  {
    label: EProductName.UCAT,
    items: getSelectItem(EEntitlementType, [
      EEntitlementType.VERBAL_REASONING,
      EEntitlementType.QUANTITATIVE_REASONING,
      EEntitlementType.DECISION_MAKING,
      EEntitlementType.SITUATIONAL_JUDGEMENT,
    ]),
    dbType: DB_TYPE.MRCP,
  },
  {
    label: EProductName.UCAT,
    items: getSelectItem(EEntitlementType, [
      EEntitlementType.VERBAL_REASONING,
      EEntitlementType.QUANTITATIVE_REASONING,
      EEntitlementType.DECISION_MAKING,
      EEntitlementType.SITUATIONAL_JUDGEMENT,
    ]),
    dbType: DB_TYPE.UCAT,
  },
];

const contentTypes = getSelectItem(EContentType, [EContentType.MARKDOWN, EContentType.CSV]);
const dbTypes = getSelectItem(DB_TYPE, [DB_TYPE.FINALS, DB_TYPE.MRCP]);
const batchTypes = getSelectItem(EBatchType, [EBatchType.QUESTION, EBatchType.STATION, EBatchType.CHAPTER]);
const quesProducts = getSelectItem(EProductType, [
  EProductType.MEDICAL_SCIENCES,
  EProductType.ANATOMY,
  EProductType.QBANK,
  EProductType.OSCE,
  EProductType.INTERVIEW_ANAESTHETICS,
  EProductType.INTERVIEW_CST,
  EProductType.INTERVIEW_IMT,
  EProductType.INTERVIEW_RADIOLOGY,
  EProductType.INTERVIEW_PAEDIATRICS,
  EProductType.INTERVIEW_ACCS,
  EProductType.MSRA,
  EProductType.PLAB1,
  EProductType.PLAB2,
  EProductType.MRCP_PART1,
  EProductType.MRCP_PART2,
  EProductType.PACES,
  EProductType.UCAT,
  EProductType.MRCP_VIDEO,
]);

const productList = getSelectItem(EProductType, [
  EProductType.ALL,
  EProductType.QBANK,
  EProductType.BUNDLE,
  EProductType.UNDERGRAD_BUNDLE,
  EProductType.COMPLETE_CLINICAL_BUNDLE,
  EProductType.OSCE,
  EProductType.ANATOMY,
  EProductType.ANATOMY_BUNDLE,
  EProductType.MEDICAL_SCIENCES,
  EProductType.INTERVIEW_ANAESTHETICS,
  EProductType.INTERVIEW_CST,
  EProductType.INTERVIEW_IMT,
  EProductType.INTERVIEW_RADIOLOGY,
  EProductType.INTERVIEW_PAEDIATRICS,
  EProductType.INTERVIEW_ACCS,
  EProductType.MSRA,
  EProductType.PLAB1,
  EProductType.PLAB2,
  EProductType.MRCP_PART1,
  EProductType.MRCP_PART2,
  EProductType.MRCP_BUNDLE,
  EProductType.PACES,
  EProductType.UCAT,
  EProductType.MRCP_VIDEO,
]);

const entitlementMapping: Record<
  EEntitlementType,
  { product: EProductType; typeId: ETopicType | EOsceType | EPaceType; undergrad: boolean }
> = {
  [EEntitlementType.ALL]: { product: EProductType.ALL, typeId: ETopicType.ALL, undergrad: false },
  [EEntitlementType.CLINICAL]: {
    product: EProductType.QBANK,
    typeId: ETopicType.CLINICAL,
    undergrad: true,
  },
  [EEntitlementType.DATA_INTERPRETATION]: {
    product: EProductType.QBANK,
    typeId: ETopicType.DATA_INTERPRETATION,
    undergrad: true,
  },
  [EEntitlementType.PRE_CLINICAL]: {
    product: EProductType.MEDICAL_SCIENCES,
    typeId: ETopicType.PRE_CLINICAL,
    undergrad: true,
  },
  [EEntitlementType.ANATOMY]: {
    product: EProductType.ANATOMY,
    typeId: ETopicType.ANATOMY,
    undergrad: true,
  },
  [EEntitlementType.CLINICAL_SUBSET]: {
    product: EProductType.MEDICAL_SCIENCES,
    typeId: ETopicType.CLINICAL,
    undergrad: true,
  },
  [EEntitlementType.CLINICAL_PROBLEM_SOLVING]: {
    product: EProductType.MSRA,
    typeId: ETopicType.CLINICAL,
    undergrad: false,
  },
  [EEntitlementType.PROFESSIONAL_DILEMMA]: {
    product: EProductType.MSRA,
    typeId: ETopicType.SJT,
    undergrad: false,
  },
  [EEntitlementType.EXTENDED_MATCHING_QUESTIONS]: {
    product: EProductType.MSRA,
    typeId: ETopicType.CLINICAL,
    undergrad: false,
  },
  [EEntitlementType.HISTORY_TAKING]: {
    product: EProductType.OSCE,
    typeId: EOsceType.HISTORY_TAKING,
    undergrad: true,
  },
  [EEntitlementType.COMMUNICATION_SKILLS]: {
    product: EProductType.OSCE,
    typeId: EOsceType.COMMUNICATION_SKILLS,
    undergrad: true,
  },
  [EEntitlementType.EXAMINATION]: {
    product: EProductType.OSCE,
    typeId: EOsceType.EXAMINATION,
    undergrad: true,
  },
  [EEntitlementType.PROCEDURE]: {
    product: EProductType.OSCE,
    typeId: EOsceType.PROCEDURE,
    undergrad: true,
  },
  [EEntitlementType.INTERPRETATION]: {
    product: EProductType.OSCE,
    typeId: EOsceType.INTERPRETATION,
    undergrad: true,
  },
  [EEntitlementType.ABCDE]: { product: EProductType.OSCE, typeId: EOsceType.ABCDE, undergrad: true },
  [EEntitlementType.PRESCRIBING]: {
    product: EProductType.OSCE,
    typeId: EOsceType.PRESCRIBING,
    undergrad: true,
  },
  [EEntitlementType.MIXED]: { product: EProductType.OSCE, typeId: EOsceType.MIXED, undergrad: true },
  [EEntitlementType.ABDOMINAL]: {
    product: EProductType.PACES,
    typeId: EPaceType.ABDOMINAL,
    undergrad: false,
  },
  [EEntitlementType.CARDIOVASCULAR]: {
    product: EProductType.PACES,
    typeId: EPaceType.CARDIOVASCULAR,
    undergrad: false,
  },
  [EEntitlementType.COMMUNICATION]: {
    product: EProductType.PACES,
    typeId: EPaceType.COMMUNICATION,
    undergrad: false,
  },
  [EEntitlementType.CONSULTATION]: {
    product: EProductType.PACES,
    typeId: EPaceType.CONSULTATION,
    undergrad: false,
  },
  [EEntitlementType.NEUROLOGY]: {
    product: EProductType.PACES,
    typeId: EPaceType.NEUROLOGY,
    undergrad: false,
  },
  [EEntitlementType.RESPIRATORY]: {
    product: EProductType.PACES,
    typeId: EPaceType.RESPIRATORY,
    undergrad: false,
  },
  [EEntitlementType.MRCP_PART_1]: {
    product: EProductType.MRCP_PART1,
    typeId: ETopicType.CLINICAL,
    undergrad: false,
  },
  [EEntitlementType.MRCP_PART_2]: {
    product: EProductType.MRCP_PART2,
    typeId: ETopicType.CLINICAL,
    undergrad: false,
  },
  [EEntitlementType.INTERVIEW_ANAESTHETICS]: {
    product: EProductType.INTERVIEW_ANAESTHETICS,
    typeId: ETopicType.INTERVIEW_ANAESTHETICS,
    undergrad: false,
  },
  [EEntitlementType.INTERVIEW_CST]: {
    product: EProductType.INTERVIEW_CST,
    typeId: ETopicType.INTERVIEW_CST,
    undergrad: false,
  },
  [EEntitlementType.INTERVIEW_IMT]: {
    product: EProductType.INTERVIEW_IMT,
    typeId: ETopicType.INTERVIEW_IMT,
    undergrad: false,
  },
  [EEntitlementType.INTERVIEW_RADIOLOGY]: {
    product: EProductType.INTERVIEW_RADIOLOGY,
    typeId: ETopicType.INTERVIEW_RADIOLOGY,
    undergrad: false,
  },
  [EEntitlementType.INTERVIEW_PAEDIATRICS]: {
    product: EProductType.INTERVIEW_PAEDIATRICS,
    typeId: ETopicType.INTERVIEW_PAEDIATRICS,
    undergrad: false,
  },
  [EEntitlementType.INTERVIEW_ACCS]: {
    product: EProductType.INTERVIEW_ACCS,
    typeId: ETopicType.INTERVIEW_ACCS,
    undergrad: false,
  },
  [EEntitlementType.PLAB_1]: { product: EProductType.PLAB1, typeId: ETopicType.CLINICAL, undergrad: false },
  [EEntitlementType.VERBAL_REASONING]: {
    product: EProductType.UCAT,
    typeId: ETopicType.SJT,
    undergrad: false,
  },
  [EEntitlementType.QUANTITATIVE_REASONING]: {
    product: EProductType.UCAT,
    typeId: ETopicType.SJT,
    undergrad: false,
  },
  [EEntitlementType.DECISION_MAKING]: {
    product: EProductType.UCAT,
    typeId: ETopicType.SJT,
    undergrad: false,
  },
  [EEntitlementType.SITUATIONAL_JUDGEMENT]: {
    product: EProductType.UCAT,
    typeId: ETopicType.SJT,
    undergrad: false,
  },
  [EEntitlementType.MRCP_VIDEO]: {
    product: EProductType.MRCP_VIDEO,
    typeId: ETopicType.CLINICAL,
    undergrad: false,
  },
  [EEntitlementType.MRCP_PART2_VIDEO]: {
    product: EProductType.MRCP_VIDEO,
    typeId: ETopicType.CLINICAL,
    undergrad: false,
  },
};

const mockProductMapping: Partial<
  Record<EProductType, { mockTypeId: EMockTestType; typeId: ETopicType; grammarType: EGrammarType }>
> = {
  [EProductType.QBANK]: {
    mockTypeId: EMockTestType.FINALS,
    typeId: ETopicType.CLINICAL,
    grammarType: EGrammarType.QUESTION,
  },
  [EProductType.MEDICAL_SCIENCES]: {
    mockTypeId: EMockTestType.UNIVERSITY_SPECIFIC,
    typeId: ETopicType.PRE_CLINICAL,
    grammarType: EGrammarType.QUESTION,
  },
  [EProductType.ANATOMY]: {
    mockTypeId: EMockTestType.ANATOMY_SPOTTER,
    typeId: ETopicType.ANATOMY,
    grammarType: EGrammarType.QUESTION,
  },
  [EProductType.MRCP_PART1]: {
    mockTypeId: EMockTestType.MRCP,
    typeId: ETopicType.CLINICAL,
    grammarType: EGrammarType.QUESTION,
  },
  [EProductType.MRCP_PART2]: {
    mockTypeId: EMockTestType.MRCP,
    typeId: ETopicType.CLINICAL,
    grammarType: EGrammarType.QUESTION,
  },
  [EProductType.PLAB1]: {
    mockTypeId: EMockTestType.UKMLA_FULL_MOCK,
    typeId: ETopicType.CLINICAL,
    grammarType: EGrammarType.QUESTION,
  },
  [EProductType.UCAT]: {
    mockTypeId: EMockTestType.UCAT_FULL_MOCK,
    typeId: ETopicType.UCAT,
    grammarType: EGrammarType.QUESTION,
  },
  [EProductType.MSRA]: {
    mockTypeId: EMockTestType.MSRA_MOCK,
    typeId: ETopicType.CLINICAL,
    grammarType: EGrammarType.QUESTION,
  },
};

const productMapping: Partial<Record<EProductType, { grammarTypes: ISelectItemSection[] }>> = {
  [EProductType.QBANK]: {
    grammarTypes: [templateGrammars.chapterGrammar, templateGrammars.questionGrammar],
  },
  [EProductType.MEDICAL_SCIENCES]: {
    grammarTypes: [templateGrammars.chapterGrammar, templateGrammars.questionGrammar],
  },
  [EProductType.ANATOMY]: {
    grammarTypes: [templateGrammars.chapterGrammar, templateGrammars.questionGrammar],
  },
  [EProductType.OSCE]: {
    grammarTypes: [templateGrammars.osceGrammar],
  },
  [EProductType.MRCP_PART1]: {
    grammarTypes: [templateGrammars.chapterGrammar, templateGrammars.questionGrammar],
  },
  [EProductType.MRCP_PART2]: {
    grammarTypes: [templateGrammars.chapterGrammar, templateGrammars.questionGrammar],
  },
  [EProductType.PACES]: {
    grammarTypes: [templateGrammars.chapterGrammar, templateGrammars.paceGrammar],
  },
  [EProductType.INTERVIEW_ANAESTHETICS]: {
    grammarTypes: [templateGrammars.chapterGrammar],
  },
  [EProductType.INTERVIEW_CST]: {
    grammarTypes: [templateGrammars.chapterGrammar],
  },
  [EProductType.INTERVIEW_IMT]: {
    grammarTypes: [templateGrammars.chapterGrammar],
  },
  [EProductType.INTERVIEW_RADIOLOGY]: {
    grammarTypes: [templateGrammars.chapterGrammar],
  },
  [EProductType.INTERVIEW_PAEDIATRICS]: {
    grammarTypes: [templateGrammars.chapterGrammar],
  },
  [EProductType.INTERVIEW_ACCS]: {
    grammarTypes: [templateGrammars.chapterGrammar],
  },
  [EProductType.MSRA]: {
    grammarTypes: [templateGrammars.chapterGrammar, templateGrammars.msraGrammar],
  },
  [EProductType.PLAB1]: {
    grammarTypes: [templateGrammars.chapterGrammar, templateGrammars.questionGrammar],
  },
  [EProductType.PLAB2]: {
    grammarTypes: [templateGrammars.osceGrammar],
  },
  [EProductType.UCAT]: {
    grammarTypes: [
      templateGrammars.verbalGrammar,
      templateGrammars.quantitativeGrammar,
      templateGrammars.decisionGrammar,
      templateGrammars.sjtGrammar,
    ],
  },
  [EProductType.VERBAL_REASONING]: {
    grammarTypes: [templateGrammars.verbalGrammar],
  },
  [EProductType.QUANTITATIVE_REASONING]: {
    grammarTypes: [templateGrammars.quantitativeGrammar],
  },
  [EProductType.DECISION_MAKING]: {
    grammarTypes: [templateGrammars.decisionGrammar],
  },
  [EProductType.SITUATIONAL_JUDGEMENT]: {
    grammarTypes: [templateGrammars.sjtGrammar],
  },
};

const dbConfig: Record<DB_TYPE, { names: EProductName[]; products: EProductType[] }> = {
  [DB_TYPE.FINALS]: {
    names: [
      EProductName.QBANK,
      EProductName.PRE_CLINICAL,
      EProductName.ANATOMY,
      EProductName.MSRA,
      EProductName.OSCE,
      EProductName.PLAB1,
      EProductName.PLAB2,
      EProductName.OTHER,
      EProductName.INTERVIEW,
    ],
    products: [
      EProductType.QBANK,
      EProductType.MEDICAL_SCIENCES,
      EProductType.ANATOMY,
      EProductType.MSRA,
      EProductType.OSCE,
      EProductType.PLAB1,
      EProductType.PLAB2,
      EProductType.INTERVIEW_ANAESTHETICS,
      EProductType.INTERVIEW_CST,
      EProductType.INTERVIEW_IMT,
      EProductType.INTERVIEW_PAEDIATRICS,
      EProductType.INTERVIEW_RADIOLOGY,
      EProductType.INTERVIEW_ACCS,
    ],
  },
  [DB_TYPE.MRCP]: {
    names: [
      EProductName.MRCP,
      EProductName.MRCP_PART1,
      EProductName.MRCP_PART2,
      EProductName.MRCP_VIDEO,
      EProductName.PACES,
      EProductName.OTHER,
    ],
    products: [EProductType.MRCP_PART1, EProductType.MRCP_PART2, EProductType.PACES, EProductType.MRCP_VIDEO],
  },
  [DB_TYPE.UCAT]: {
    names: [
      EProductName.UCAT,
      EProductName.VERBAL_REASONING,
      EProductName.DECISION_MAKING,
      EProductName.QUANTITATIVE_REASONING,
      EProductName.SITUATIONAL_JUDGEMENT,
      EProductName.OTHER,
    ],
    products: [
      EProductType.UCAT,
      EProductType.VERBAL_REASONING,
      EProductType.DECISION_MAKING,
      EProductType.QUANTITATIVE_REASONING,
      EProductType.SITUATIONAL_JUDGEMENT,
    ],
  },
};

const getOptions = (
  options: ISelectItemSection[] | undefined,
  dbType = DB_TYPE.FINALS,
): ISelectItemSection[] => {
  if (!options) {
    return [];
  }

  return options.filter((o) => dbConfig[dbType]?.names?.includes(o.label as never));
};

const gradProducts: Record<EProductCategory, EProductType[]> = {
  [EProductCategory.ALL]: [],
  [EProductCategory.UNDERGRAD]: [
    EProductType.QBANK,
    EProductType.BUNDLE,
    EProductType.UNDERGRAD_BUNDLE,
    EProductType.COMPLETE_CLINICAL_BUNDLE,
    EProductType.MEDICAL_SCIENCES,
    EProductType.ANATOMY,
    EProductType.ANATOMY_BUNDLE,
    EProductType.OSCE,
  ],
  [EProductCategory.POSTGRAD]: [
    EProductType.MRCP_PART1,
    EProductType.MRCP_PART2,
    EProductType.MRCP_BUNDLE,
    EProductType.PACES,
    EProductType.MSRA,
    EProductType.PLAB1,
    EProductType.PLAB2,
    EProductType.INTERVIEW_ANAESTHETICS,
    EProductType.INTERVIEW_CST,
    EProductType.INTERVIEW_IMT,
    EProductType.INTERVIEW_PAEDIATRICS,
    EProductType.INTERVIEW_RADIOLOGY,
    EProductType.INTERVIEW_ACCS,
    EProductType.MRCP_VIDEO,
  ],
  [EProductCategory.STUDENT]: [
    EProductType.UCAT,
    EProductType.VERBAL_REASONING,
    EProductType.DECISION_MAKING,
    EProductType.QUANTITATIVE_REASONING,
    EProductType.SITUATIONAL_JUDGEMENT,
  ],
  [EProductCategory.CLINICAL]: [
    EProductType.QBANK,
    EProductType.OSCE,
    EProductType.BUNDLE,
    EProductType.COMPLETE_CLINICAL_BUNDLE,
    EProductType.UNDERGRAD_BUNDLE,
  ],
};

export {
  batchTypes,
  contentTypes,
  dbConfig,
  dbTypes,
  difficulty,
  durations,
  entitlementMapping,
  getOptions,
  gradProducts,
  grammarOptions,
  mockProductMapping,
  mockTestTypes,
  productEntitlements,
  productList,
  productMapping,
  productOptions,
  quesProducts,
  questionTypes,
  templates,
  topicOptions,
};

export const STATE_OPTIONS = [
  { label: '-', key: 0 },
  { label: 'No action needed', key: 1 },
  { label: 'Deleted', key: 2 },
  { label: 'Amended', key: 3 },
];

export const ACTIONED_BY_OPTIONS = [
  { label: '-', key: 0 },
  { label: 'Agent', key: 1 },
  { label: 'Human', key: 2 },
];
